import React from 'react'
import * as RadixNavMenu from '@radix-ui/react-navigation-menu'
import { dropdownBaseStyle, dropdownItemStyle, dropdownMenuStyle } from './dropdown'
import { SmartLinkOptional } from './link'
import { Popover } from '../../layouts/components/popover'

interface NavMenuGroupProps {
  children: React.ReactNode
  className?: string
}

/**
 * The NavMenuGroup component is a wrapper for a series of NavMenu items.
 */
export const NavMenuGroup: React.FC<NavMenuGroupProps> = ({ children, className }) => {
  const vp = 'data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut origin-[top_center]'
  // the var is the size of the content, and we want padding so we add it back here
  const swipeAnim =
    'h-[calc(var(--radix-navigation-menu-viewport-height)+0.5rem+2px)] w-[calc(var(--radix-navigation-menu-viewport-width)+0.5rem+2px)] overflow-hidden will-change-transform'
  return (
    <RadixNavMenu.Root className={`${className || ''}`} delayDuration={0}>
      <RadixNavMenu.List className="flex flex-row items-center gap-3">
        {children}

        <RadixNavMenu.Indicator className="top-full z-10 flex h-2.5 items-end justify-center overflow-hidden transition-[width,transform_50ms_ease] data-[state=hidden]:animate-fadeOutFast data-[state=visible]:animate-fadeIn">
          <Popover
            arrow={false} // doesnt play well with animations
            collisionPadding={5}
            backgroundStyle="z-[2000]"
            open={true}
            sideOffset={-5}
            trigger={<div className="pointer-events-none absolute inset-0" />}
            onFocus={(e) => e.stopPropagation()} // dont let this focus event close the nav menu (since its portaled, it counts as focus outside the nav menu)
            onOpenAutoFocus={(e) => e.preventDefault()} // dont interfere with nav menu focus
            onCloseAutoFocus={(e) => e.preventDefault()} // dont interfere with nav menu focus
          >
            <RadixNavMenu.Viewport
              className={`viewport hide-if-empty !min-w-[0] ${vp} ${swipeAnim} ${dropdownMenuStyle}`}
            />
          </Popover>
        </RadixNavMenu.Indicator>
      </RadixNavMenu.List>
    </RadixNavMenu.Root>
  )
}

interface NavMenuProps {
  /**  The content of the NavMenu, which is shown when the button is active. */
  children: React.ReactNode
  /** The button that triggers the NavMenu. */
  button: React.ReactNode
  /** Additional classes for styling the NavMenu. */
  className?: string
}

/**
 * A NavMenu wraps a button trigger and some content that is shown when the button active in the nav menu.
 * This must be used inside a NavMenuGroup.
 */
export const NavMenu: React.FC<NavMenuProps> = ({ children, button, className }) => {
  const mc =
    'data-[motion=from-start]:animate-enterFromLeft data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToLeft data-[motion=to-end]:animate-exitToRight'
  return (
    <RadixNavMenu.Item className={className}>
      <RadixNavMenu.Trigger asChild>{button}</RadixNavMenu.Trigger>
      {/* We hide outside the viewport because the viewport is inside the indicator, and it tries to render the content
      just in-place for a second before the indicator mounts // these are absolute so that the slide in/out animations
      work properly */}
      <RadixNavMenu.Content className={`hidden-outside-viewport absolute left-1 top-1 max-h-[80vh] ${mc}`}>
        {children}
      </RadixNavMenu.Content>
    </RadixNavMenu.Item>
  )
}

type NavActionProps = {
  children: React.ReactNode
}

/**
 * A NavAction is a wrapper for a link or button that is used inside a NavMenu.
 */
export const NavAction: React.FC<NavActionProps> = ({ children }) => {
  return <RadixNavMenu.Link asChild>{children}</RadixNavMenu.Link>
}

type NavLinkProps = NavActionProps & {
  to?: string
  className?: string
  style?: string
}

/**
 * A NavLink is a link that is used inside a NavMenu.
 */
export const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ children, to, className, style = dropdownItemStyle }, ref) => {
    return (
      <NavAction>
        <SmartLinkOptional to={to} ref={ref} className={`no-underline ${className} ${dropdownBaseStyle} ${style}`}>
          {children}
        </SmartLinkOptional>
      </NavAction>
    )
  }
)

interface NavItemProps {
  children: React.ReactNode
  className?: string
}

/**
 * A NavItem is a wrapper for an item that does not have a link or button.
 * It is used inside a NavMenu.
 */
export const NavItem: React.FC<NavItemProps> = ({ children, className }) => {
  return <div className={`${dropdownBaseStyle} ${className || ''}`}>{children}</div>
}
